// src/context/modules/imagesModule.js
import { useState, useCallback } from "react";
import {
  fetchImages,
  getPresignedUploadUrl,
  uploadToS3,
  saveImageMetadata,
  updateImage,
  deleteImage,
} from "../../api/imagesApi";

export const useImagesModule = () => {
  // State
  const [images, setImages] = useState([]);
  const [imagesLoading, setImagesLoading] = useState(true);
  const [uploadProgress, setUploadProgress] = useState(0);

  // Operations
  const refreshImages = useCallback(async () => {
    setImagesLoading(true);
    try {
      const fetchedImages = await fetchImages();
      setImages(fetchedImages);
      return fetchedImages;
    } catch (error) {
      console.error("Error refreshing images:", error);
      throw error;
    } finally {
      setImagesLoading(false);
    }
  }, []);

  // Upload an image with progress tracking
  const addImage = useCallback(
    async (file, metadata = {}) => {
      if (!file) {
        throw new Error("No file provided");
      }

      setImagesLoading(true);
      setUploadProgress(0);

      try {
        // Step 1: Get presigned URL for upload
        const presignedData = await getPresignedUploadUrl({
          fileName: file.name,
          fileType: file.type,
        });

        // Step 2: Upload to S3 with progress tracking
        await uploadToS3(presignedData.uploadUrl, file, (progress) =>
          setUploadProgress(progress)
        );

        setUploadProgress(100);

        // Step 3: Save metadata to your server
        const savedImage = await saveImageMetadata({
          fileKey: presignedData.fileKey,
          fileName: file.name,
          description: metadata.description || "",
          altText: metadata.altText || "",
          fileType: file.type,
          fileSize: file.size,
        });

        setImages((prev) => [...prev, savedImage]);
        return savedImage;
      } catch (error) {
        console.error("Error uploading image:", error);
        throw error;
      } finally {
        await refreshImages();
        setImagesLoading(false);
      }
    },
    [refreshImages]
  );

  const updateImageData = useCallback(
    async (id, imageData) => {
      setImagesLoading(true);
      try {
        const updatedImage = await updateImage(id, imageData);
        setImages((prev) =>
          prev.map((image) => (image.id === id ? updatedImage : image))
        );
        return updatedImage;
      } catch (error) {
        console.error("Error updating image:", error);
        throw error;
      } finally {
        await refreshImages();
        setImagesLoading(false);
      }
    },
    [refreshImages]
  );

  const removeImage = useCallback(
    async (id) => {
      setImagesLoading(true);
      try {
        await deleteImage(id);
        setImages((prev) => prev.filter((image) => image.id !== id));
      } catch (error) {
        console.error("Error deleting image:", error);
        throw error;
      } finally {
        await refreshImages();
        setImagesLoading(false);
      }
    },
    [refreshImages]
  );

  // Get image by ID
  const getImageById = useCallback(
    (id) => {
      return images.find((img) => img.id === id) || null;
    },
    [images]
  );

  // Get image by aws_location
  const getImageByKey = useCallback(
    (fileKey) => {
      if (!fileKey) return null;
      return images.find((img) => img.aws_location === fileKey) || null;
    },
    [images]
  );

  // Generate a direct URL for displaying an image
  const getImageUrl = useCallback((fileKey) => {
    if (!fileKey) return null;

    // If it's already a complete URL, return it as is
    if (fileKey.startsWith("http")) {
      return fileKey;
    }

    // Otherwise, assume it's a file key and construct the full URL
    // This should be configured to point to your S3 bucket URL
    return `https://barber-shop-images.s3.amazonaws.com/${fileKey}`;
  }, []);

  // Initial data load
  const loadInitialImages = async () => {
    try {
      setImagesLoading(true);
      const imagesData = await fetchImages();
      setImages(imagesData);
    } catch (error) {
      console.error("Error loading images:", error);
    } finally {
      setImagesLoading(false);
    }
  };

  return {
    // State
    images,
    imagesLoading,
    uploadProgress,

    // Operations
    refreshImages,
    addImage,
    updateImage: updateImageData,
    removeImage,
    getImageById,
    getImageByKey,
    getImageUrl,
    loadInitialImages,
  };
};
